import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  limit,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from './auth'
import type { AuditLog } from '@/types'

export const useAuditLogStore = defineStore('auditLog', () => {
  const entries = ref<AuditLog[]>([])
  const filterEntityType = ref<string | null>(null)
  const loading = ref(false)

  let unsubscribe: Unsubscribe | null = null

  const filteredEntries = computed(() => {
    if (!filterEntityType.value) return entries.value
    return entries.value.filter((e) => e.entity_type === filterEntityType.value)
  })

  function subscribeToAuditLog(incidentId: string) {
    if (unsubscribe) unsubscribe()

    loading.value = true
    const q = query(
      collection(db, 'incidents', incidentId, 'audit_log'),
      orderBy('timestamp', 'desc'),
      limit(200),
    )

    unsubscribe = onSnapshot(q, (snapshot) => {
      entries.value = snapshot.docs.map((doc) => ({
        log_id: doc.id,
        ...(doc.data() as Omit<AuditLog, 'log_id'>),
      }))
      loading.value = false
    })
  }

  function setFilter(entityType: string) {
    filterEntityType.value = entityType
  }

  function clearFilter() {
    filterEntityType.value = null
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    entries.value = []
    filterEntityType.value = null
  }

  return {
    entries,
    filterEntityType,
    loading,
    filteredEntries,
    subscribeToAuditLog,
    setFilter,
    clearFilter,
    teardown,
  }
})

// Standalone write helper — imported by other stores
export async function writeAuditLog(
  incidentId: string,
  action: string,
  entityType: string,
  entityId: string,
  before: Record<string, unknown> | null = null,
  after: Record<string, unknown> | null = null,
): Promise<void> {
  const authStore = useAuthStore()
  const user = authStore.currentUser
  const profile = authStore.userProfile

  const userName = profile
    ? `${profile.user_first_name} ${profile.user_last_name}`.trim()
    : 'Unknown'

  await addDoc(collection(db, 'incidents', incidentId, 'audit_log'), {
    user_id: user?.uid ?? 'unknown',
    user_name: userName,
    action,
    entity_type: entityType,
    entity_id: entityId,
    before,
    after,
    timestamp: serverTimestamp(),
  })
}
